import { Injectable } from "@angular/core";
import { Conf2AccountJwtVO, RoomId, RtmpDestinationKey } from "@common/domain";
import {
  Actions,
  concatLatestFrom,
  createEffect,
  EffectNotification,
  ofType,
  OnRunEffects
} from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { fetch } from "@nrwl/angular";
import { AccountJwtService } from "@youm-front/account-jwt/data-access";
import { Conf2Selectors } from "@youm-front/conf2/data-access";
import {
  Conf2RtmpDestinationCreateRequestDTO,
  DestinationsActions,
  DestinationsService
} from "@youm-front/destinations/data-access";
import { Observable } from "rxjs";
import {
  switchMap,
  exhaustMap,
  takeUntil,
  filter,
  take,
  mergeMap,
  catchError
} from "rxjs/operators";
import { RoomActions, RoomMixerActions } from "../actions";
import { RoomService } from "../services";

@Injectable()
export class RoomLiveEffects implements OnRunEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly roomService: RoomService,
    private readonly store: Store,
    private readonly destinationService: DestinationsService,
    private readonly accountJwtService: AccountJwtService
  ) {}

  startLiveStream$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomActions.startLiveStream),
      concatLatestFrom(() =>
        this.accountJwtService.anyAccountJwtVO$.pipe(filter((_) => !!_))
      ),
      switchMap(([action, accountJwt]) =>
        (accountJwt instanceof Conf2AccountJwtVO
          ? this.conf2StartLiveStream(action.roomId)
          : this.roomService.startLiveStream(action.roomId)
        ).pipe(
          mergeMap((_) => [
            // May create new destination for conf2 speaker,
            // reload rtmp destinations to see it.
            DestinationsActions.loadRtmpDestinations(),
            RoomActions.startLiveStreamSuccess({
              room: _
            })
          ]),
          // Inside switchMap: a failure must not complete the effect, or
          // every later Go Live would be ignored until reload.
          catchError((_) => [
            RoomActions.startLiveStreamFailure({
              code: _?.code,
              message: _?.message,
              untilNextLiveMs: _?.untilNextLiveMs
            })
          ])
        )
      )
    )
  );

  stopLiveStream$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomActions.stopLiveStream),
      fetch({
        run: (action) => {
          return this.roomService.stopLiveStream(action.roomId).pipe(
            switchMap(() => [
              RoomActions.stopLiveStreamSuccess({
                roomId: action.roomId
              }),
              RoomMixerActions.roomMixerDelete()
            ])
          );
        },
        onError: (action, error) =>
          RoomActions.stopLiveStreamFailure({
            error: error.message
          })
      })
    )
  );

  ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>) {
    return this.actions$.pipe(
      ofType(RoomActions.init),
      exhaustMap(() =>
        resolvedEffects$.pipe(
          takeUntil(this.actions$.pipe(ofType(RoomActions.destroy)))
        )
      )
    );
  }

  private conf2StartLiveStream(roomId: RoomId) {
    return this.store.select(Conf2Selectors.conf2StreamNameQueryParam).pipe(
      mergeMap((_) =>
        _
          ? this.destinationService
              .rtmpDestinationCreate(
                new Conf2RtmpDestinationCreateRequestDTO({
                  key: _ as unknown as RtmpDestinationKey,
                  roomId
                })
              )
              .pipe(mergeMap(() => this.roomService.startLiveStream(roomId)))
          : this.roomService.startLiveStream(roomId)
      ),
      take(1)
    );
  }
}
